import React, { useEffect, useState, useRef } from 'react';
import { CHARACTERS } from '../logic/gameData';
import gsap from 'gsap';
import { FallingLeaves, MotionLines, Flash, Vignette, Overlay } from '../common/Effects';
import { AnimatedBackground } from '../common/AnimatedBackgrounds';

export default function Story({ gameLogic, onLeaveStory }) {
  const [currentEvent, setCurrentEvent] = useState(null);
  const [location, setLocation] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentEffects, setCurrentEffects] = useState({
    fallingLeaves: null,
    motionLines: null,
    screenShake: null,
    flash: null,
    vignette: null,
    overlay: null
  });
  
  const locationRef = useRef();
  const characterRefs = useRef({});
  const dialogRef = useRef();
  const choicesRef = useRef();
  const containerRef = useRef();
  const backgroundRef = useRef();
  const effectRefs = useRef({});
  const typingTimeoutRef = useRef(null);

  const cleanupScene = () => {
    // Kill all GSAP animations
    gsap.killTweensOf(locationRef.current);
    gsap.killTweensOf(containerRef.current);
    gsap.killTweensOf(dialogRef.current);
    Object.values(characterRefs.current).forEach(ref => gsap.killTweensOf(ref));
    Object.values(effectRefs.current).forEach(ref => gsap.killTweensOf(ref));
    if (backgroundRef.current?.imageRef) {
      gsap.killTweensOf(backgroundRef.current.imageRef);
    }

    // Clear all state
    setLocation(null);
    setCharacters([]);
    setCurrentSpeaker(null);
    setDialogVisible(false);
    setCurrentEffects({
      fallingLeaves: null,
      motionLines: null,
      screenShake: null,
      flash: null,
      vignette: null,
      overlay: null
    });
  };

  useEffect(() => {
    // window.gameAudio.playMusic("story");

    const current = gameLogic.getCurrentContent();
    setCurrentEvent(current.event);

    return () => {
      cleanupScene();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!currentEvent) return;

    switch (currentEvent.type) {
      case 'showLocation':
        if (location !== currentEvent.locationId) {
          setLocation(currentEvent.locationId);
          gsap.fromTo(locationRef.current,
            { opacity: 0 },
            { opacity: 1, duration: 0.3, onComplete: handleAdvance }
          );
        } else {
          handleAdvance();
        }
        break;

      case 'hideLocation':
        gsap.to(locationRef.current,
          { 
            opacity: 0, 
            duration: 0.3,
            onComplete: () => {
              setLocation(null);
              handleAdvance();
            }
          }
        );
        break;

      case 'showCharacter':
        const newCharacter = {
          id: currentEvent.characterId,
          variation: currentEvent.variation,
          position: currentEvent.position
        };

        // Check if character is already at the correct position with correct variation
        let existingCharacter = characters.find(char =>
          char.id === currentEvent.characterId &&
          char.position === currentEvent.position
        );

        // If character is already correctly positioned, skip everything
        if (existingCharacter) {
          // Update the state for the variation
          setCharacters(prev => {
            const filtered = prev.filter(char =>
              char.id !== newCharacter.id && char.position !== newCharacter.position
            );
            return [...filtered, newCharacter];
          });
          handleAdvance();
          break;
        }

        // Then handle animations
        const timeline = gsap.timeline({
          onComplete: handleAdvance
        });

        // Find if character exists anywhere on screen
        existingCharacter = characters.find(char => char.id === currentEvent.characterId);
        if (existingCharacter) {
          timeline.to(characterRefs.current[existingCharacter.position],
            { opacity: 0, duration: 0.3 }
          );
        }

        // Find if any character is in the target position
        const characterAtPosition = characters.find(char =>
          char.position === currentEvent.position && char.id !== currentEvent.characterId
        );
        if (characterAtPosition) {
          timeline.to(characterRefs.current[characterAtPosition.position],
            { opacity: 0, duration: 0.3 },
            existingCharacter ? ">" : 0
          );
        }

        // Finally update the state
        setCharacters(prev => {
          const filtered = prev.filter(char =>
            char.id !== newCharacter.id && char.position !== newCharacter.position
          );
          return [...filtered, newCharacter];
        });

        timeline.fromTo(characterRefs.current[newCharacter.position],
          { opacity: 0 },
          { opacity: 1, duration: 0.5 },
          ">"
        );
        break;

      case 'hideCharacter':
        setCharacters(prev => prev.filter(char =>
          !(char.id === currentEvent.characterId && char.position === currentEvent.position)
        ));

        if (characterRefs.current[currentEvent.position]?.id === currentEvent.characterId) {
          gsap.to(characterRefs.current[currentEvent.position],
            {
              opacity: 0,
              duration: 0.5,
              onComplete: handleAdvance
            }
          );
        } else {
          handleAdvance();
        }
        break;

      case 'showDialog':
      case 'showNarration':
        setCurrentSpeaker(currentEvent.type === 'showDialog' ? currentEvent.speaker : null);

        if (currentEvent.type === 'showDialog' && currentEvent.variation) {
          setCharacters(prev => prev.map(char =>
            char.id === currentEvent.speaker
              ? { ...char, variation: currentEvent.variation }
              : char
          ));
        }

        if (!dialogVisible) {
          gsap.fromTo(dialogRef.current,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.15 }
          );
        }
        setDialogVisible(true);
        setTypedText('');
        setIsTyping(true);

        if (gameLogic.getToTypeText()) {
          let currentText = '';
          const fullText = currentEvent.text;
          const interval = 1000 / gameLogic.getTypingSpeed();
          let charIndex = 0;

          const typeChar = () => {
            if (charIndex < fullText.length) {
              currentText += fullText[charIndex];
              setTypedText(currentText);
              charIndex++;
              typingTimeoutRef.current = setTimeout(typeChar, interval);
            } else {
              setIsTyping(false);
            }
          };

          typeChar();
        } else {
          setTypedText(currentEvent.text);
          setIsTyping(false);
        }
        break;

      case 'showEffect':
        if (currentEvent.effect === 'screenShake') {
          const intensity = currentEvent.intensity || 10;
          const duration = currentEvent.duration || 0.5;
          const shakeTimeline = gsap.timeline();

          for (let i = 0; i < duration * 10; i++) {
            shakeTimeline.to(containerRef.current, {
              x: Math.random() * intensity - intensity/2,
              y: Math.random() * intensity - intensity/2,
              duration: 0.05
            });
          }

          shakeTimeline.to(containerRef.current, {
            x: 0,
            y: 0,
            duration: 0.1,
            onComplete: () => {
              if (currentEvent.waitUntilFinish !== false) {
                handleAdvance();
              }
            }
          });

          if (currentEvent.waitUntilFinish === false) {
            handleAdvance();
          }
        } else if (currentEvent.effect === 'movement') {
          gsap.to(backgroundRef.current.imageRef, {
            x: `${currentEvent.x}%`,
            duration: currentEvent.duration || 1,
            ease: 'power1.inOut',
            onComplete: () => {
              if (currentEvent.waitUntilFinish !== false) {
                handleAdvance();
              }
            }
          });

          if (currentEvent.waitUntilFinish === false) {
            handleAdvance();
          }
        } else if (currentEvent.effect === 'zoom') {
          gsap.to(backgroundRef.current.imageRef, {
            scale: currentEvent.scale || 1,
            duration: currentEvent.duration || 1,
            ease: 'power1.inOut',
            onComplete: () => {
              if (currentEvent.waitUntilFinish !== false) {
                handleAdvance();
              }
            }
          });

          if (currentEvent.waitUntilFinish === false) {
            handleAdvance();
          }
        } else if (currentEvent) {
          setCurrentEffects(prev => ({
            ...prev,
            [currentEvent.effect]: {
              ...currentEvent
            }
          }));

          if (currentEvent.duration) {
            setTimeout(() => {
              setCurrentEffects(prev => ({
                ...prev,
                [currentEvent.effect]: null
              }));

              if (currentEvent.waitUntilFinish !== false) {
                handleAdvance();
              }
            }, currentEvent.duration * 1000);
          }

          if (!currentEvent.duration || currentEvent.waitUntilFinish === false) {
            handleAdvance();
          }
        }
        break;

      case 'hideEffect':
        if (effectRefs.current[currentEvent.effect]) {
          gsap.to(effectRefs.current[currentEvent.effect], {
            opacity: 0,
            duration: currentEvent.fadeOutDuration || 0.3,
            onComplete: () => {
              setCurrentEffects(prev => ({
                ...prev,
                [currentEvent.effect]: null
              }));
              handleAdvance();
            }
          });
        } else {
          setCurrentEffects(prev => ({
            ...prev,
            [currentEvent.effect]: null
          }));
          handleAdvance();
        }
        break;

      default:
        throw new Error(`Unrecognized event type: ${currentEvent.type}`);
    }
  }, [currentEvent]);

  const handleAdvance = () => {
    if (isTyping) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      setTypedText(currentEvent.text);
      setIsTyping(false);
      return;
    }

    const currentChapter = gameLogic.position.chapter;
    const result = gameLogic.advance();

    if (result.isGameOver) {
      onLeaveStory();
      return;
    }

    const current = gameLogic.getCurrentContent();
    const nextEventType = current.event.type;

    // Check if we're changing chapters
    if (gameLogic.position.chapter !== currentChapter) {
      cleanupScene();
    }

    if (!['showDialog', 'showNarration'].includes(nextEventType)) {
      setDialogVisible(false);
      setCurrentSpeaker(null);
    }

    setCurrentEvent(current.event);
  };

  const handleChoice = (index) => {
    // window.gameAudio.playSound('smallClick')
    const currentChapter = gameLogic.position.chapter;
    const result = gameLogic.makeChoice(index);

    if (result.isGameOver) {
      onLeaveStory();
      return;
    }

    const current = gameLogic.getCurrentContent();
    const nextEventType = current.event.type;

    // Check if we're changing chapters
    if (gameLogic.position.chapter !== currentChapter) {
      cleanupScene();
    }

    if (!['showDialog', 'showNarration'].includes(nextEventType)) {
      setDialogVisible(false);
      setCurrentSpeaker(null);
    }

    setCurrentEvent(current.event);
  };

  const handleDialogClick = (e) => {
    if (isTyping) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      setTypedText(currentEvent.text);
      setIsTyping(false);
    } else if (!currentEvent.choices) {
      // window.gameAudio.playSound('smallClick')
      handleAdvance();
    }
  };

  if (!currentEvent) return null;

  const positions = ['left', 'center', 'right'];
  const getCharacterPosition = (position) => {
    switch (position) {
      case 'left': return 'left-1/4';
      case 'right': return 'left-3/4';
      default: return 'left-1/2';
    }
  };

  return (
    <div className="fixed inset-0 w-screen h-[100svh] overflow-hidden" ref={containerRef}>
      <div className="w-screen h-[100svh] relative">
        {/* Background Layer - z-0 */}
        <div className="absolute inset-0 z-[0] pointer-events-none" ref={locationRef}>
          {location && (
            <>
              <AnimatedBackground
                ref={backgroundRef}
                imageSrc={gameLogic.getSpriteUrl(location)}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/40" />
              <div className="absolute inset-0 bg-[linear-gradient(transparent_98%,rgba(74,158,255,0.1)_100%)] bg-[length:100%_4px] animate-scan pointer-events-none" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,transparent_0%,rgba(0,0,0,0.4)_100%)]" />
            </>
          )}
        </div>

        {/* Space Particles Effect */}
        <div className="absolute inset-0 z-[10] pointer-events-none">
          <FallingLeaves 
            color="#4a9eff" 
            count={15} 
          />
        </div>

        {/* Character Layer - z-20/30 */}
        {positions.map(position => (
          <div 
            key={position}
            className={`absolute bottom-[-50px] ${getCharacterPosition(position)} transform -translate-x-1/2 pointer-events-none`}
            ref={el => characterRefs.current[position] = el}
            style={{
              width: '416px', 
              height: '624px',
              zIndex: currentSpeaker === (characters.find(c => c.position === position)?.id) ? 30 : 20
            }}
          >
            {characters.map(char => {
              if (char.position === position) {
                return (
                  <img
                    key={char.id}
                    src={gameLogic.getSpriteUrl(char.id, char.variation)}
                    alt={CHARACTERS[char.id].name}
                    className="transition-all duration-300"
                    style={{
                      filter: currentSpeaker 
                        ? (currentSpeaker === char.id 
                          ? 'brightness(1.1) drop-shadow(0 0 10px rgba(74,158,255,0.3))' 
                          : 'brightness(0.6)')
                        : 'brightness(1)'
                    }}
                  />
                );
              }
              return null;
            })}
          </div>
        ))}

        {/* Effects Layer - z-40 */}
        <div className="absolute inset-0 z-[40] pointer-events-none">
          {currentEffects.fallingLeaves && (
            <FallingLeaves 
              color={currentEffects.fallingLeaves.color}
              count={currentEffects.fallingLeaves.count}
            />
          )}
          {currentEffects.motionLines && (
            <MotionLines 
              duration={currentEffects.motionLines.duration} 
              rotation={currentEffects.motionLines.rotation} 
            />
          )}
          {currentEffects.flash && (
            <Flash 
              color={currentEffects.flash.color}
              duration={currentEffects.flash.duration}
            />
          )}
          {currentEffects.vignette && (
            <Vignette 
              intensity={currentEffects.vignette.intensity}
            />
          )}
          {currentEffects.overlay && (
            <div ref={el => effectRefs.current['overlay'] = el}>
              <Overlay 
                overlayType={currentEffects.overlay.overlayType}
                opacity={currentEffects.overlay.opacity}
                gameLogic={gameLogic}
              />
            </div>
          )}
        </div>

        {/* Choices Layer - z-50 */}
        {!isTyping && currentEvent.choices && (
          <div className="absolute inset-0 flex items-center justify-center z-[50]">
            <div className="space-y-4 transform transition-all duration-300 opacity-0 animate-fade-in w-full max-w-[600px] px-4">
              {currentEvent.choices.map((choice, index) => (
                <button
                  key={index}
                  onClick={() => handleChoice(index)}
                  className="w-full group relative overflow-hidden
                           backdrop-blur-sm bg-[#4a9eff10] 
                           border border-[#4a9eff40] hover:border-[#4a9eff]
                           py-4 px-8 rounded-lg text-white text-left
                           font-['Quicksand'] font-medium
                           transform transition-all duration-300 hover:scale-[1.02]
                           active:scale-95"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#4a9eff20] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#4a9eff50] to-transparent" />
                  <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#4a9eff30] to-transparent" />
                  {choice.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Dialog Layer - z-40 */}
        {(currentEvent.type === 'showDialog' || currentEvent.type === 'showNarration') && (
          <div 
            className="absolute bottom-0 left-0 w-full p-4 cursor-pointer z-[40]" 
            ref={dialogRef} 
            onClick={handleDialogClick}
          >
            <div className="relative backdrop-blur-md bg-[#0a0a1599] rounded-xl p-6 text-white border border-[#4a9eff20] shadow-lg">
              <div className="absolute -top-0.5 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#4a9eff50] to-transparent" />
              <div className="absolute -bottom-0.5 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#4a9eff30] to-transparent" />
              
              {currentEvent.type === 'showDialog' && (
                <div className="mb-3 font-['Orbitron'] font-bold text-lg tracking-wide">
                  <span className="text-[#4a9eff]">{CHARACTERS[currentEvent.speaker].name}</span>
                </div>
              )}

              <div className={`mb-4 text-lg font-['Quicksand'] leading-relaxed
                            ${currentEvent.type === 'showNarration' ? 'italic text-[#4a9eff99]' : 'text-[#ffffff99]'}`}>
                {typedText}
              </div>

              {!isTyping && !currentEvent.choices && (
                <div className="absolute bottom-6 right-6">
                  <div className="animate-pulse-subtle text-[#4a9eff] hover:text-[#4a9eff] text-2xl">
                    →
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes scan {
          from { background-position: 0 0; }
          to { background-position: 0 -100%; }
        }
        @keyframes pulse-subtle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-scan {
          animation: scan 10s linear infinite;
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 2s ease-in-out infinite;
        }
        .animate-fade-in {
          animation: fade-in 0.3s forwards;
        }
      `}</style>
    </div>
  );
}
